import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Input,
  Label,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@b2b/ui-components';
import { Save, Loader2, X, Pencil, AlertTriangle, Eye } from 'lucide-react';
import { useStorefrontSettings } from './hooks/useStorefrontSettings';
import { sellerService, authService, SellerStatus, UserRole, type User } from '@b2b/services';
import type { StorefrontBanner } from './types/storefront.types';

const BANNER_HINT =
  'We have to limit height to maintain consistency. In some device both side of the banner might be cropped for height limitation.';

/** Image MIME types allowed by default server media config (see `MEDIA_ALLOWED_TYPES` / `configuration.ts`). */
const BANNER_FILE_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';
const BANNER_ACCEPTED_FORMATS_LABEL = 'JPEG, PNG, WebP, GIF';

const TOP_BANNER_MAX = 1;
const SLIDER_BANNER_MAX = 5;

const PENDING_TOP_ID = '__pending-top__';

const MEDIA_ID_PATTERN = /^[a-z0-9]{25}$/;
const LEGACY_UPLOAD_SEGMENT = '/public/uploads/all/';
const S3_UPLOAD_SEGMENT = '/old_uploads/all/';
const DEFAULT_MEDIA_S3_BASE_URL =
  'https://nyc3.digitaloceanspaces.com/bizcover-media-uploads';
const DEFAULT_MEDIA_PUBLIC_BASE_URL = 'https://bizcoverindia.com/public';

function getMediaS3BaseUrl(): string {
  return (
    import.meta.env.VITE_MEDIA_S3_BASE_URL ||
    import.meta.env.VITE_DO_SPACES_PUBLIC_BASE_URL ||
    DEFAULT_MEDIA_S3_BASE_URL
  ).replace(/\/+$/, '');
}

function getMediaPublicBaseUrl(): string {
  return (
    import.meta.env.VITE_MEDIA_PUBLIC_BASE_URL || DEFAULT_MEDIA_PUBLIC_BASE_URL
  ).replace(/\/+$/, '');
}

/** Resolve stored banner values (absolute URL, relative path, legacy upload path, media id) for `<img src>`. */
function resolveBannerPreviewSrcSync(rawUrl: string | null | undefined): string | null {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const value = rawUrl.trim();
  if (!value) return null;

  if (
    value.startsWith('blob:') ||
    value.startsWith('data:') ||
    value.startsWith('http://') ||
    value.startsWith('https://')
  ) {
    return value;
  }
  if (value.startsWith('//')) {
    return `https:${value}`;
  }

  const mediaS3Base = getMediaS3BaseUrl();
  if (value.includes(LEGACY_UPLOAD_SEGMENT)) {
    const suffix = value.split(LEGACY_UPLOAD_SEGMENT)[1];
    if (suffix) return `${mediaS3Base}${S3_UPLOAD_SEGMENT}${suffix}`;
  }
  if (value.includes(S3_UPLOAD_SEGMENT)) {
    const suffix = value.split(S3_UPLOAD_SEGMENT)[1];
    if (suffix) return `${mediaS3Base}${S3_UPLOAD_SEGMENT}${suffix}`;
  }

  if (MEDIA_ID_PATTERN.test(value)) {
    return null;
  }

  const publicBase = getMediaPublicBaseUrl();
  const cleanPath = value.startsWith('/') ? value.slice(1) : value;
  return `${publicBase}/${cleanPath}`;
}

function useBannerPreviewSrc(
  rawUrl: string | null | undefined,
  localBlobUrl?: string | null,
): { previewSrc: string | null; loading: boolean } {
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localBlobUrl) {
      setPreviewSrc(localBlobUrl);
      setLoading(false);
      return;
    }

    if (!rawUrl?.trim()) {
      setPreviewSrc(null);
      setLoading(false);
      return;
    }

    const syncResolved = resolveBannerPreviewSrcSync(rawUrl);
    if (syncResolved) {
      setPreviewSrc(syncResolved);
      setLoading(false);
      return;
    }

    const trimmed = rawUrl.trim();
    if (!MEDIA_ID_PATTERN.test(trimmed)) {
      setPreviewSrc(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    sellerService
      .getMedia(trimmed)
      .then((result) => {
        if (cancelled) return;
        const candidate = result?.url || result?.thumbnailUrl || '';
        setPreviewSrc(resolveBannerPreviewSrcSync(candidate) ?? candidate ?? null);
      })
      .catch(() => {
        if (!cancelled) setPreviewSrc(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rawUrl, localBlobUrl]);

  return { previewSrc, loading };
}

type TopBannerPendingState =
  | { kind: 'none' }
  | { kind: 'upsert'; imageUrl: string }
  | { kind: 'deleteSaved' };

/**
 * Matches backend `isSellerWithPendingApproval` (auth.service): seller roles with
 * `PENDING_APPROVAL` or `null` sellerStatus are awaiting admin approval.
 */
function isSellerAwaitingAdminApproval(user: User): boolean {
  const role = String(user.role ?? '');
  const isSellerLike =
    role === UserRole.SELLER ||
    role === 'seller' ||
    role === 'SELLER' ||
    role === 'BUYER_SELLER' ||
    role === 'buyer_seller' ||
    role === 'both';
  if (!isSellerLike) return false;
  const s = user.sellerStatus;
  if (
    s === SellerStatus.APPROVED ||
    s === SellerStatus.REJECTED ||
    s === SellerStatus.SUSPENDED
  ) {
    return false;
  }
  // `null`, `undefined`, or `PENDING_APPROVAL` — awaiting admin (aligned with API)
  return true;
}

export interface StorefrontManagementPageProps {
  /** Called after storefront settings are saved successfully (e.g. to refetch /me for onboarding step 3). */
  onStorefrontSaved?: () => void;
}

export default function StorefrontManagementPage({
  onStorefrontSaved,
}: StorefrontManagementPageProps = {}) {
  const [sellerId, setSellerId] = useState<string | null>(null);
  /** Until `ready`, treat uploads as disabled to avoid a flash of enabled controls for pending sellers. */
  const [uploadGate, setUploadGate] = useState<'loading' | 'allowed' | 'blocked'>('loading');
  const [banners, setBanners] = useState<StorefrontBanner[]>([]);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [saveBasicInfoLoading, setSaveBasicInfoLoading] = useState(false);
  const [saveBannersLoading, setSaveBannersLoading] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [seoErrors, setSeoErrors] = useState<{ title?: string; description?: string }>({});
  /** Last saved SEO values (for view mode + cancel). Synced after load and after successful save. */
  const [seoBaseline, setSeoBaseline] = useState({ title: '', description: '' });
  const [seoMetaEditing, setSeoMetaEditing] = useState(false);

  /** Banner images are staged here until the user clicks Save (per upload/remove). */
  const [topPending, setTopPending] = useState<TopBannerPendingState>({ kind: 'none' });
  const [pendingSliderRemovals, setPendingSliderRemovals] = useState<string[]>([]);
  const [pendingSliderAdds, setPendingSliderAdds] = useState<string[]>([]);

  const uploadBlocked = uploadGate === 'blocked';

  const settings = useStorefrontSettings({
    initialCompanyDescription: '',
    initialGallery: [],
    initialCertifications: [],
    initialBusinessInfo: {},
    onImageUpload: async (file: File) => {
      if (uploadGate !== 'allowed') {
        throw new Error(
          'Your seller account is waiting for administrator approval. File uploads are unavailable until your account is approved.'
        );
      }
      const result = await sellerService.uploadMedia(file);
      return result.url;
    },
  });

  // Capture baselines after initial GET completes — do not depend on isLoading true→false (fast/cached responses can batch so that transition never runs).
  useEffect(() => {
    if (settings.storefrontSettingsLoadVersion < 1) return;
    setSeoBaseline({
      title: settings.seoTitle,
      description: settings.seoDescription,
    });
    setSeoMetaEditing(false);
    // Intentionally only storefrontSettingsLoadVersion: avoids resetting baseline on every keystroke, and fixes missing transition when isLoading never renders true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.storefrontSettingsLoadVersion]);

  // Auto-dismiss toast and cleanup timer to avoid leaks on unmount/re-renders.
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const hasPersistedSeoMeta =
    seoBaseline.title.trim() !== '' && seoBaseline.description.trim() !== '';
  const seoFieldsLocked = hasPersistedSeoMeta && !seoMetaEditing;

  // Seller ID for "visit shop" link + upload gate (pending seller approval)
  useEffect(() => {
    let cancelled = false;
    const loadMe = async () => {
      try {
        const meResponse = await authService.getMe();
        if (cancelled) return;
        if (meResponse.status === 'active' && meResponse.user?.id) {
          setSellerId(meResponse.user.id);
        }
        const blocked =
          meResponse.status === 'pending_registration' ||
          (meResponse.user ? isSellerAwaitingAdminApproval(meResponse.user) : false);
        setUploadGate(blocked ? 'blocked' : 'allowed');
      } catch {
        if (!cancelled) {
          setSellerId(null);
          setUploadGate('allowed');
        }
      }
    };
    loadMe();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load banners
  useEffect(() => {
    const loadBanners = async () => {
      setBannersLoading(true);
      try {
        const res = await sellerService.getStorefrontBanners();
        const list = Array.isArray(res?.data) ? res.data : [];
        setBanners(list);
      } catch {
        setBanners([]);
      } finally {
        setBannersLoading(false);
      }
    };
    loadBanners();
  }, []);

  const topBanner = banners.find((b) => b.order === 0) ?? null;
  const sliderBanners = banners.filter((b) => b.order >= 1).sort((a, b) => a.order - b.order);

  const effectiveTopBanner = useMemo((): StorefrontBanner | null => {
    if (topPending.kind === 'upsert') {
      if (topBanner) {
        return { ...topBanner, imageUrl: topPending.imageUrl };
      }
      return {
        id: PENDING_TOP_ID,
        imageUrl: topPending.imageUrl,
        order: 0,
        isActive: true,
      };
    }
    if (topPending.kind === 'deleteSaved') {
      return null;
    }
    return topBanner;
  }, [topBanner, topPending]);

  const bannersDirty = useMemo(
    () =>
      topPending.kind !== 'none' ||
      pendingSliderRemovals.length > 0 ||
      pendingSliderAdds.length > 0,
    [topPending.kind, pendingSliderRemovals.length, pendingSliderAdds.length]
  );

  const handleTopImageStaged = useCallback((imageUrl: string) => {
    setTopPending({ kind: 'upsert', imageUrl });
  }, []);

  const handleTopRemoveStaged = useCallback(() => {
    setTopPending((prev) => {
      if (prev.kind === 'upsert') {
        return { kind: 'none' };
      }
      if (topBanner) {
        return { kind: 'deleteSaved' };
      }
      return { kind: 'none' };
    });
  }, [topBanner]);

  const handleStageSliderRemove = useCallback((bannerId: string) => {
    setPendingSliderRemovals((r) => (r.includes(bannerId) ? r : [...r, bannerId]));
  }, []);

  const handleStageSliderAdd = useCallback((imageUrl: string) => {
    setPendingSliderAdds((urls) => [...urls, imageUrl]);
  }, []);

  const handleUnstageSliderAddAt = useCallback((index: number) => {
    setPendingSliderAdds((urls) => urls.filter((_, i) => i !== index));
  }, []);

  const handleSaveBanners = useCallback(async () => {
    if (!bannersDirty) return;
    setSaveBannersLoading(true);
    setToast(null);
    try {
      if (topPending.kind === 'upsert') {
        if (topBanner) {
          await sellerService.updateStorefrontBanner(topBanner.id, {
            ...topBanner,
            imageUrl: topPending.imageUrl,
          });
        } else {
          await sellerService.createStorefrontBanner({
            imageUrl: topPending.imageUrl,
            order: 0,
            isActive: true,
          });
        }
      } else if (topPending.kind === 'deleteSaved' && topBanner) {
        await sellerService.deleteStorefrontBanner(topBanner.id);
      }

      for (const id of pendingSliderRemovals) {
        await sellerService.deleteStorefrontBanner(id);
      }

      const activeSliderOrders = sliderBanners
        .filter((b) => b.order >= 1 && !pendingSliderRemovals.includes(b.id))
        .map((b) => b.order);
      let nextOrder =
        activeSliderOrders.length === 0 ? 0 : Math.max(...activeSliderOrders);

      for (const url of pendingSliderAdds) {
        nextOrder += 1;
        await sellerService.createStorefrontBanner({
          imageUrl: url,
          order: nextOrder,
          isActive: true,
        });
      }

      const res = await sellerService.getStorefrontBanners();
      const list = Array.isArray(res?.data) ? res.data : [];
      setBanners(list);
      setTopPending({ kind: 'none' });
      setPendingSliderRemovals([]);
      setPendingSliderAdds([]);
      setToast({ type: 'success', message: 'Banner settings saved.' });
    } catch (e: any) {
      setToast({ type: 'error', message: e?.message || 'Failed to save banners.' });
    } finally {
      setSaveBannersLoading(false);
    }
  }, [
    bannersDirty,
    topPending,
    topBanner,
    pendingSliderRemovals,
    pendingSliderAdds,
    sliderBanners,
  ]);

  const handleCancelBanners = useCallback(async () => {
    setTopPending({ kind: 'none' });
    setPendingSliderRemovals([]);
    setPendingSliderAdds([]);
    setToast(null);
    try {
      const res = await sellerService.getStorefrontBanners();
      const list = Array.isArray(res?.data) ? res.data : [];
      setBanners(list);
    } catch {
      setToast({ type: 'error', message: 'Failed to reload banners.' });
    }
  }, []);

  // Basic Info save
  const handleSaveBasicInfo = useCallback(async () => {
    const title = settings.seoTitle.trim();
    const description = settings.seoDescription.trim();
    const nextErrors: { title?: string; description?: string } = {};

    if (!title) {
      nextErrors.title = 'Please enter Meta Title';
    }
    if (!description) {
      nextErrors.description = 'Please enter Meta Description';
    }

    if (nextErrors.title || nextErrors.description) {
      setSeoErrors(nextErrors);
      setToast(null);
      return;
    }

    setSeoErrors({});
    setSaveBasicInfoLoading(true);
    setToast(null);
    try {
      await settings.saveBasicInfo({
        shopName: settings.shopName,
        shopPhone: settings.shopPhone,
        shopAddress: settings.shopAddress,
        logoUrl: settings.logoUrl ?? undefined,
        seoTitle: title,
        seoDescription: description,
      });
      setToast({ type: 'success', message: 'SEO & meta saved successfully.' });
      setSeoBaseline({
        title,
        description,
      });
      settings.setSeoTitle(title);
      settings.setSeoDescription(description);
      setSeoMetaEditing(false);
      onStorefrontSaved?.();
    } catch (e: any) {
      setToast({ type: 'error', message: e?.message || 'Failed to save.' });
    } finally {
      setSaveBasicInfoLoading(false);
    }
  }, [
    settings.shopName,
    settings.shopPhone,
    settings.shopAddress,
    settings.logoUrl,
    settings.seoTitle,
    settings.seoDescription,
    settings.saveBasicInfo,
    settings.setSeoTitle,
    settings.setSeoDescription,
    onStorefrontSaved,
  ]);

  const handleCancelSeoMeta = useCallback(() => {
    settings.setSeoTitle(seoBaseline.title);
    settings.setSeoDescription(seoBaseline.description);
    setSeoErrors({});
    setSeoMetaEditing(false);
  }, [seoBaseline.title, seoBaseline.description, settings.setSeoTitle, settings.setSeoDescription]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Page title and visit shop - same as Profile Settings */}
        <div className="mb-6">
          <h1 className="text-base font-semibold text-gray-800">
            Shop Settings
          </h1>
          {sellerId && (
            <Link
              to={`/seller_detail/${sellerId}`}
              className="text-sm text-gray-500 hover:text-gray-700 mt-1 inline-block transition-colors"
            >
              (visit shop →)
            </Link>
          )}
        </div>

        {uploadBlocked && (
          <div
            className="mb-4 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
            role="alert"
          >
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
            <div>
              <p className="font-medium text-amber-900">Account pending approval</p>
              <p className="mt-1 text-amber-900/90">
                Your seller account is waiting for administrator approval. Banner image uploads are
                disabled until your account is approved. You can still edit and save SEO text below.
              </p>
            </div>
          </div>
        )}

        {toast && (
          <div
            className={`mb-4 px-4 py-3 rounded-lg border ${
              toast.type === 'success'
                ? 'bg-green-50 text-green-800 border-green-200'
                : 'bg-red-50 text-red-800 border-red-200'
            }`}
          >
            {toast.message}
          </div>
        )}

        {/* Section 1: SEO / Meta (name, logo, phone, address are in Profile Settings) */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800">
              SEO & Meta
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Shop name, logo, phone and address are managed in{' '}
              <Link to="/seller/profile_settings" className="text-primary hover:underline">
                Profile Settings
              </Link>
              .
            </p>
          </div>
          <div className="px-6 py-6 space-y-4">
            <div>
              <Label className="text-sm text-gray-700">
                Meta Title {!seoFieldsLocked && <span className="text-red-500">*</span>}
              </Label>
              <Input
                value={settings.seoTitle}
                onChange={(e) => {
                  settings.setSeoTitle(e.target.value);
                  if (seoErrors.title) {
                    setSeoErrors((prev) => ({ ...prev, title: undefined }));
                  }
                }}
                readOnly={seoFieldsLocked}
                className={`mt-2 rounded-lg ${
                  seoErrors.title ? 'border-red-500 focus-visible:ring-red-500/20' : 'border-gray-300'
                } ${seoFieldsLocked ? 'bg-gray-50 text-gray-700 cursor-default' : ''}`}
                placeholder="Meta title"
              />
              {seoErrors.title && (
                <p className="mt-1 text-xs text-red-600">{seoErrors.title}</p>
              )}
            </div>
            <div>
              <Label className="text-sm text-gray-700">
                Meta description {!seoFieldsLocked && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                value={settings.seoDescription}
                onChange={(e) => {
                  settings.setSeoDescription(e.target.value);
                  if (seoErrors.description) {
                    setSeoErrors((prev) => ({ ...prev, description: undefined }));
                  }
                }}
                readOnly={seoFieldsLocked}
                rows={3}
                className={`mt-2 w-full rounded-lg border px-3 py-2 text-sm text-gray-900 focus:border-orange-500 focus:ring-orange-500/50 focus-visible:border-orange-500 focus-visible:ring-orange-500/50 focus-visible:ring-[3px] ${
                  seoErrors.description
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-primary focus:ring-primary/20'
                } ${
                  seoFieldsLocked ? 'bg-gray-50 text-gray-700 cursor-default' : 'bg-white'
                }`}
                placeholder="Meta description"
              />
              {seoErrors.description && (
                <p className="mt-1 text-xs text-red-600">{seoErrors.description}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            {hasPersistedSeoMeta && !seoMetaEditing ? (
              <Button
                type="button"
                onClick={() => {
                  setSeoErrors({});
                  setSeoMetaEditing(true);
                }}
                variant="primary"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            ) : (
              <>
                {hasPersistedSeoMeta && seoMetaEditing && (
                  <Button
                    type="button"
                    onClick={handleCancelSeoMeta}
                    disabled={saveBasicInfoLoading}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleSaveBasicInfo}
                  disabled={saveBasicInfoLoading}
                  variant="primary"
                >
                  {saveBasicInfoLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </section>

        {/* Section 2: Banner Settings */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800">
              Banner Settings
            </h2>
          </div>
          {bannersLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <div className="px-6 py-6 space-y-5">
                <div>
                  <Label className="text-sm text-gray-700">Top Banner (1920×390)</Label>
                  <p className="text-sm text-gray-500 mt-1 mb-2">{BANNER_HINT}</p>
                  <p className="text-xs text-gray-600 mb-1">
                    Accepted file types: {BANNER_ACCEPTED_FORMATS_LABEL}.
                  </p>
                  <p className="text-xs text-gray-600 mb-2">Max {TOP_BANNER_MAX} image.{topBanner?.imageUrl ? ' Replace by uploading a new image.' : ''}</p>
                  <BannerUpload
                    banner={effectiveTopBanner}
                    order={0}
                    accept={BANNER_FILE_ACCEPT}
                    uploadDisabled={uploadGate !== 'allowed'}
                    onError={(message) => setToast({ type: 'error', message })}
                    onImageStaged={handleTopImageStaged}
                    onRemove={handleTopRemoveStaged}
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-700">
                    Slider Banners (1150×450) —{' '}
                    {
                      sliderBanners.filter((b) => !pendingSliderRemovals.includes(b.id)).length +
                      pendingSliderAdds.length
                    }
                    /{SLIDER_BANNER_MAX}
                  </Label>
                  <p className="text-sm text-gray-500 mt-1 mb-2">{BANNER_HINT}</p>
                  <p className="text-xs text-gray-600 mb-1">
                    Accepted file types: {BANNER_ACCEPTED_FORMATS_LABEL}.
                  </p>
                  <p className="text-xs text-gray-600 mb-2">Max {SLIDER_BANNER_MAX} images.</p>
                  <SliderBannersEditor
                    sliderBanners={sliderBanners}
                    pendingRemovals={pendingSliderRemovals}
                    pendingAddUrls={pendingSliderAdds}
                    fileAccept={BANNER_FILE_ACCEPT}
                    maxCount={SLIDER_BANNER_MAX}
                    uploadDisabled={uploadGate !== 'allowed'}
                    onMaxReached={() => setToast({ type: 'error', message: 'Maximum 5 slider banners allowed.' })}
                    onError={(message) => setToast({ type: 'error', message })}
                    onStageRemove={handleStageSliderRemove}
                    onStageAddUrl={handleStageSliderAdd}
                    onUnstagePendingAddAt={handleUnstageSliderAddAt}
                  />
                </div>
              </div>
              {bannersDirty && (
                <div className="mx-6 mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                  Your uploaded or removed images are not saved yet. Click <strong>Save</strong> to apply
                  changes, or <strong>Cancel</strong> to discard them.
                </div>
              )}
              <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                {bannersDirty && (
                  <Button
                    type="button"
                    onClick={handleCancelBanners}
                    disabled={saveBannersLoading}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleSaveBanners}
                  disabled={saveBannersLoading || !bannersDirty}
                  variant="primary"
                >
                  {saveBannersLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

// Resolved banner image preview with optional full-size modal.
function BannerImagePreview({
  imageUrl,
  localBlobUrl,
  alt,
  thumbnailClassName,
  onRemove,
  replaceControl,
}: {
  imageUrl?: string | null;
  localBlobUrl?: string | null;
  alt: string;
  thumbnailClassName: string;
  onRemove?: () => void;
  replaceControl?: React.ReactNode;
}) {
  const { previewSrc, loading } = useBannerPreviewSrc(imageUrl, localBlobUrl);
  const [previewOpen, setPreviewOpen] = useState(false);
  const hasSource = Boolean((imageUrl && imageUrl.trim()) || localBlobUrl);

  if (!hasSource) return null;

  return (
    <>
      <BannerPreviewThumbnail
        loading={loading}
        previewSrc={previewSrc}
        alt={alt}
        thumbnailClassName={thumbnailClassName}
        onRemove={onRemove}
        onPreview={() => previewSrc && setPreviewOpen(true)}
        replaceControl={replaceControl}
      />
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden bg-black/95 border-none">
          <DialogHeader className="sr-only">
            <DialogTitle>{alt}</DialogTitle>
          </DialogHeader>
          {previewSrc ? (
            <img
              src={previewSrc}
              alt={alt}
              className="w-full max-h-[85vh] object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function BannerPreviewThumbnail({
  loading,
  previewSrc,
  alt,
  thumbnailClassName,
  onRemove,
  onPreview,
  replaceControl,
}: {
  loading: boolean;
  previewSrc: string | null;
  alt: string;
  thumbnailClassName: string;
  onRemove?: () => void;
  onPreview: () => void;
  replaceControl?: React.ReactNode;
}) {
  return (
    <div className="relative inline-block group">
      {loading ? (
        <BannerPreviewLoading thumbnailClassName={thumbnailClassName} />
      ) : previewSrc ? (
        <img src={previewSrc} alt={alt} className={thumbnailClassName} />
      ) : (
        <BannerPreviewFallback thumbnailClassName={thumbnailClassName} />
      )}
      <BannerPreviewActions
        previewSrc={previewSrc}
        onRemove={onRemove}
        onPreview={onPreview}
        replaceControl={replaceControl}
      />
    </div>
  );
}

function BannerPreviewLoading({ thumbnailClassName }: { thumbnailClassName: string }) {
  return (
    <div
      className={`${thumbnailClassName} flex items-center justify-center bg-gray-100 border border-gray-200`}
    >
      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  );
}

function BannerPreviewFallback({ thumbnailClassName }: { thumbnailClassName: string }) {
  return (
    <div
      className={`${thumbnailClassName} flex items-center justify-center bg-gray-100 border border-gray-200 text-xs text-gray-500`}
    >
      Preview unavailable
    </div>
  );
}

function BannerPreviewActions({
  previewSrc,
  onRemove,
  onPreview,
  replaceControl,
}: {
  previewSrc: string | null;
  onRemove?: () => void;
  onPreview: () => void;
  replaceControl?: React.ReactNode;
}) {
  return (
    <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
      {previewSrc ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onPreview}
          className="bg-white text-gray-900 hover:bg-gray-100"
        >
          <Eye className="h-4 w-4 mr-1" />
          Preview
        </Button>
      ) : null}
      {replaceControl}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="rounded-lg bg-black/60 text-white p-1.5 hover:bg-black/80"
          aria-label="Remove banner"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

// Single banner (top or one slider): show image or upload (image is staged in parent until Save)
function BannerUpload({
  banner,
  order,
  accept,
  uploadDisabled,
  onImageStaged,
  onRemove,
  onError,
}: {
  banner: StorefrontBanner | null;
  order: number;
  accept?: string;
  /** When true (loading or pending seller approval), file upload is not available. */
  uploadDisabled?: boolean;
  onImageStaged: (imageUrl: string) => void;
  onRemove: () => void;
  onError?: (message: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const fileInputId = `top-banner-upload-${order}`;

  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (uploadDisabled) {
      onError?.(
        'Your seller account is waiting for administrator approval. Uploads are unavailable until your account is approved.'
      );
      e.target.value = '';
      return;
    }

    let objectUrl: string | null = null;
    setUploading(true);
    try {
      objectUrl = URL.createObjectURL(file);
      setLocalPreviewUrl(objectUrl);
      const result = await sellerService.uploadMedia(file);
      const url = result?.url;
      if (!url || typeof url !== 'string') {
        onError?.('Upload did not return a valid URL.');
        return;
      }
      onImageStaged(url);
    } catch (err: any) {
      onError?.(err?.message || 'Failed to upload image.');
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setLocalPreviewUrl(null);
      setUploading(false);
      e.target.value = '';
    }
  };

  const hasImage = Boolean(banner?.imageUrl?.trim()) || Boolean(localPreviewUrl);

  if (hasImage) {
    return (
      <div className="space-y-2">
        <BannerImagePreview
          imageUrl={banner?.imageUrl}
          localBlobUrl={localPreviewUrl}
          alt={`Top banner ${order + 1}`}
          thumbnailClassName="max-h-40 w-full max-w-2xl rounded-lg border border-gray-200 object-cover"
          onRemove={onRemove}
          replaceControl={
            !uploadDisabled ? (
              <label
                htmlFor={fileInputId}
                className="cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Replace
              </label>
            ) : undefined
          }
        />
        {!uploadDisabled ? (
          <input
            id={fileInputId}
            type="file"
            accept={accept ?? 'image/*'}
            className="hidden"
            onChange={handleFile}
            disabled={uploading}
          />
        ) : null}
        {uploading ? (
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Uploading…
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50/50">
      <p className="text-sm text-gray-500 mb-2">{BANNER_HINT}</p>
      {uploadDisabled ? (
        <p className="text-sm text-amber-800">
          Uploads are unavailable while your seller account is pending approval.
        </p>
      ) : (
        <label className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
          <input
            type="file"
            accept={accept ?? 'image/*'}
            className="hidden"
            onChange={handleFile}
            disabled={uploading}
          />
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Browse'}
          Choose File
        </label>
      )}
    </div>
  );
}

// Slider banners: stage uploads/removals until parent Save
function SliderBannersEditor({
  sliderBanners,
  pendingRemovals,
  pendingAddUrls,
  fileAccept,
  maxCount = 5,
  uploadDisabled,
  onMaxReached,
  onError,
  onStageRemove,
  onStageAddUrl,
  onUnstagePendingAddAt,
}: {
  sliderBanners: StorefrontBanner[];
  pendingRemovals: string[];
  pendingAddUrls: string[];
  fileAccept?: string;
  maxCount?: number;
  uploadDisabled?: boolean;
  onMaxReached?: () => void;
  onError?: (message: string) => void;
  onStageRemove: (bannerId: string) => void;
  onStageAddUrl: (imageUrl: string) => void;
  onUnstagePendingAddAt: (index: number) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const visibleSaved = sliderBanners.filter((b) => !pendingRemovals.includes(b.id));
  const atLimit = visibleSaved.length + pendingAddUrls.length >= maxCount;

  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const addNew = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (uploadDisabled) {
      onError?.(
        'Your seller account is waiting for administrator approval. Uploads are unavailable until your account is approved.'
      );
      e.target.value = '';
      return;
    }
    if (atLimit) {
      onMaxReached?.();
      e.target.value = '';
      return;
    }

    let objectUrl: string | null = null;
    setUploading(true);
    try {
      objectUrl = URL.createObjectURL(file);
      setLocalPreviewUrl(objectUrl);
      const result = await sellerService.uploadMedia(file);
      const url = result?.url;
      if (!url || typeof url !== 'string') {
        onError?.('Upload did not return a valid URL.');
        return;
      }
      onStageAddUrl(url);
    } catch (err: any) {
      onError?.(err?.message || 'Failed to upload image.');
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setLocalPreviewUrl(null);
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-3">
      {visibleSaved.map((b) => (
        <div key={b.id} className="flex items-center gap-3 p-2 rounded-lg border border-gray-200 bg-gray-50">
          <BannerImagePreview
            imageUrl={b.imageUrl}
            alt={`Slider banner ${b.order}`}
            thumbnailClassName="h-24 w-auto min-w-[120px] max-w-xs rounded-lg border border-gray-200 object-cover"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onStageRemove(b.id)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Remove
          </Button>
        </div>
      ))}
      {pendingAddUrls.map((url, idx) => (
        <div
          key={`pending-slider-${idx}-${url.slice(-24)}`}
          className="flex items-center gap-3 p-2 rounded-lg border border-dashed border-gray-300 bg-gray-50/40"
        >
          <BannerImagePreview
            imageUrl={url}
            localBlobUrl={idx === pendingAddUrls.length - 1 ? localPreviewUrl : null}
            alt={`New slider banner ${idx + 1}`}
            thumbnailClassName="h-24 w-auto min-w-[120px] max-w-xs rounded-lg border border-gray-200 object-cover"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onUnstagePendingAddAt(idx)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Remove
          </Button>
        </div>
      ))}
      {uploading && localPreviewUrl && pendingAddUrls.length === 0 ? (
        <div className="flex items-center gap-3 p-2 rounded-lg border border-dashed border-gray-300 bg-gray-50/40">
          <BannerImagePreview
            localBlobUrl={localPreviewUrl}
            alt="Uploading slider banner"
            thumbnailClassName="h-24 w-auto min-w-[120px] max-w-xs rounded-lg border border-gray-200 object-cover opacity-80"
          />
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Uploading…
          </span>
        </div>
      ) : null}
      {atLimit ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50/50">
          <p className="text-sm text-gray-500">Maximum {maxCount} slider banners reached.</p>
        </div>
      ) : uploadDisabled ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50/50">
          <p className="text-sm text-amber-800">
            Uploads are unavailable while your seller account is pending approval.
          </p>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50/50">
          <label className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
            <input
              type="file"
              accept={fileAccept ?? 'image/*'}
              className="hidden"
              onChange={addNew}
              disabled={uploading}
            />
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Browse'}
            Choose File
          </label>
        </div>
      )}
    </div>
  );
}
